import { useEffect, useRef } from "react";

declare global {
  interface Window {
    AppleID?: {
      auth: {
        renderButton: (config: Record<string, unknown>) => void;
      };
    };
  }
}

const APPLE_JS_SDK =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

export function AppleSignInButton({
  onPress,
  disabled,
}: {
  onPress?: () => Promise<void>;
  disabled?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onPressRef = useRef(onPress);
  onPressRef.current = onPress;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let mounted = true;

    async function setup() {
      if (!window.AppleID) {
        await new Promise<void>((resolve, reject) => {
          const script = document.createElement("script");
          script.src = APPLE_JS_SDK;
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error("Failed to load Apple JS SDK"));
          document.body.appendChild(script);
        });
      }

      if (!mounted || !window.AppleID || !containerRef.current) return;

      const id = `apple-sign-in-${crypto.randomUUID()}`;
      containerRef.current.id = id;

      window.AppleID.auth.renderButton({
        id,
        color: "black",
        type: "sign-in",
        border: true,
        borderRadius: 16,
        width: "100%",
        height: 44,
      });

      containerRef.current.addEventListener(
        "click",
        (e) => {
          if (disabledRef.current) return;
          e.stopImmediatePropagation();
          onPressRef.current?.();
        },
        true,
      );

      containerRef.current.addEventListener(
        "keydown",
        (e) => {
          if (e.key === "Enter" || e.key === " ") {
            if (disabledRef.current) return;
            e.stopImmediatePropagation();
            e.preventDefault();
            onPressRef.current?.();
          }
        },
        true,
      );
    }

    setup();

    return () => {
      mounted = false;
    };
  }, []);

  return <div ref={containerRef} />;
}
